import { GroupMember, UserSummary } from "@coaching-culture/types";
import {
  Box,
  Button,
  Flex,
  IconButton,
  SortableTable,
  SortableTableColumn,
  Text,
} from "@coaching-culture/ui";
import { uniqBy } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { FaTimes, FaUser } from "react-icons/fa";
import { Recipient } from "./CreateCampaign";
import { UserSelectModal } from "./UserSelectModal";

export type UserSelectionTableProps = {
  value: Recipient[];
  onChange: (val: Recipient[]) => void;
};

export function UserSelectionTable({
  value,
  onChange,
}: UserSelectionTableProps) {
  const [modal, setModal] = useState<"user" | "group" | "">("");

  const removeUser = useCallback(
    (u: Recipient) => {
      onChange(value.filter((x) => x.id !== u.id));
    },
    [value, onChange],
  );

  const cols = useMemo(
    (): SortableTableColumn<UserSummary>[] => [
      {
        name: "name",
        label: "Name",
      },
      {
        name: "email",
        label: "Email",
        props: {
          style: {
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          },
        },
      },
      {
        name: "controls",
        label: "",
        props: {
          style: {
            textAlign: "right",
            width: "1px",
          },
        },
        format: (x) => (
          <IconButton
            color="danger"
            icon={FaTimes}
            onClick={() => removeUser(x)}
          />
        ),
      },
    ],
    [removeUser],
  );

  const addUsers = (users: UserSummary[] | GroupMember[]) => {
    const u = uniqBy(value.concat(users), "id").map((x) => ({
      id: x.id,
      name: x.name,
      email: x.email,
    }));
    onChange(u);
    setModal("");
  };

  return (
    <>
      {modal === "user" && <UserSelectModal onSelect={addUsers} />}
      <Flex mb={2} justifyContent="space-between" alignItems="flex-end">
        <Text>
          <strong>Users:</strong> {value.length}
        </Text>
        <Flex>
          <Button
            icon={FaUser}
            mr={1}
            color="primary"
            onClick={() => setModal("user")}
          >
            Add Users
          </Button>
        </Flex>
      </Flex>
      <Box
        style={{ overflowY: "auto" }}
        maxHeight={500}
        border={1}
        borderRadius={6}
      >
        <SortableTable<Recipient>
          columns={cols}
          data={value}
          emptyMessage="No users have been added."
        />
      </Box>
    </>
  );
}
